import { HttpClient, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AssistantChatRequest,
  AssistantMessage,
  AssistantStreamEvent,
} from '../models/assistant.model';

/**
 * How many suggested follow-ups to render, and how long one may be. Mirrors the
 * agent's own `MAX_QUESTIONS` / `MAX_LENGTH` (`assistant/follow_ups.py`) — a row
 * of chips is one line, not a paragraph.
 *
 * Over-length is **dropped, not truncated**, on both sides deliberately: a
 * question cut off mid-clause is a control whose label no longer says what
 * pressing it will send, and its text would stop matching the prompt — breaking
 * 2.5.3 Label in Name as well as the reader's expectation. Something that long
 * is prose the model put in the wrong place, and the right amount of it to show
 * is none.
 */
const FOLLOW_UP_LIMIT = 3;
const FOLLOW_UP_MAX_LENGTH = 90;

/**
 * Client for the streaming assistant backend.
 *
 * The backend responds with Server-Sent Events (text/event-stream). We consume
 * the stream via HttpClient with `reportProgress` so the existing HTTP
 * interceptors (Ping bearer token) still apply — each progress event carries the
 * cumulative body in `partialText`, which we parse into discrete SSE frames as
 * they complete.
 */
@Injectable({
  providedIn: 'root',
})
export class AssistantService {
  private readonly _http = inject(HttpClient);

  // Assistant endpoint: the SPA's CloudFront distribution under /api/v1/assistant
  // → a streaming Lambda Function URL (OAC + app-level Ping JWT). Same origin as
  // the SPA, so responses stream incrementally (token by token) with no CORS.
  private readonly _apiUrl = environment.aws.assistantEndpoint;

  /**
   * Send a prompt and stream the assistant's reply.
   *
   * @param prompt the user's current message.
   * @param history prior turns for multi-turn context (excludes the new prompt).
   * @param traceId optional correlation id forwarded to the AI gateway.
   * @returns an Observable emitting parsed stream events until completion.
   */
  streamChat(
    prompt: string,
    history: AssistantMessage[] = [],
    traceId?: string,
  ): Observable<AssistantStreamEvent> {
    const body: AssistantChatRequest = { prompt, history, trace_id: traceId };
    // Serialize once and send the exact string we hash: CloudFront's Origin
    // Access Control signs the request to the Lambda Function URL, and Lambda
    // rejects unsigned payloads — so for POST the caller must supply the body's
    // SHA-256 in x-amz-content-sha256, which CloudFront folds into its SigV4
    // signature. Passing the raw string (not the object) guarantees the bytes we
    // hash are the bytes sent.
    const bodyText = JSON.stringify(body);

    return from(this._sha256Hex(bodyText)).pipe(
      switchMap((bodyHash) => this._streamPost(bodyText, bodyHash)),
    );
  }

  /** Issue the streaming POST once the body hash is known. */
  private _streamPost(
    bodyText: string,
    bodyHash: string,
  ): Observable<AssistantStreamEvent> {
    return new Observable<AssistantStreamEvent>((subscriber) => {
      let consumed = 0;

      const subscription = this._http
        .post(this._apiUrl, bodyText, {
          observe: 'events',
          responseType: 'text',
          reportProgress: true,
          headers: {
            'Content-Type': 'application/json',
            'X-Amz-Content-Sha256': bodyHash,
          },
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.DownloadProgress) {
              const text = (event as { partialText?: string }).partialText ?? '';
              consumed = this._drainFrames(text, consumed, false, subscriber);
            } else if (event.type === HttpEventType.Response) {
              const text = (event.body as string) ?? '';
              this._drainFrames(text, consumed, true, subscriber);
              subscriber.complete();
            }
          },
          error: (err) => subscriber.error(err),
        });

      return () => subscription.unsubscribe();
    });
  }

  /** Hex-encoded SHA-256 of a UTF-8 string, via the Web Crypto API. */
  private async _sha256Hex(text: string): Promise<string> {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(text),
    );
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Parse any complete SSE frames from `text` starting at `consumed`, emit them,
   * and return the new consumed offset. Frames are separated by a blank line.
   */
  private _drainFrames(
    text: string,
    consumed: number,
    isFinal: boolean,
    subscriber: { next: (e: AssistantStreamEvent) => void },
  ): number {
    let remaining = text.slice(consumed);
    let separator = remaining.indexOf('\n\n');

    while (separator !== -1) {
      const frame = remaining.slice(0, separator);
      consumed += separator + 2;
      remaining = remaining.slice(separator + 2);
      const parsed = this._parseFrame(frame);
      if (parsed) {
        subscriber.next(parsed);
      }
      separator = remaining.indexOf('\n\n');
    }

    // On the final response, flush a trailing frame without its blank line.
    if (isFinal && remaining.trim()) {
      const parsed = this._parseFrame(remaining);
      if (parsed) {
        subscriber.next(parsed);
      }
    }

    return consumed;
  }

  /** Turn one raw SSE frame ("event: x\ndata: {...}") into a typed event. */
  private _parseFrame(frame: string): AssistantStreamEvent | null {
    let eventName = '';
    let dataRaw = '';
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataRaw = line.slice('data:'.length).trim();
      }
    }
    if (!eventName || !dataRaw) {
      return null;
    }

    let data: Record<string, unknown>;
    try {
      data = JSON.parse(dataRaw);
    } catch {
      return null;
    }

    switch (eventName) {
      case 'token':
        return { type: 'token', text: String(data['text'] ?? '') };
      case 'tool':
        return { type: 'tool', name: String(data['name'] ?? '') };
      case 'done':
        return {
          type: 'done',
          toolsUsed: Array.isArray(data['tools_used'])
            ? (data['tools_used'] as string[])
            : [],
          followUps: this._followUps(data['follow_ups']),
        };
      case 'error':
        return { type: 'error', error: String(data['error'] ?? 'Unknown error') };
      default:
        return null;
    }
  }

  /**
   * The agent's suggested next questions, taken at face value but not on trust.
   *
   * Every one of these is rendered as a control and, when pressed, sent back as
   * the user's own prompt — so this is the boundary where a payload from another
   * repo becomes UI. The agent caps and trims them already; the same limits are
   * applied here because "the other side does it" is exactly the assumption
   * that stops being true after a deploy.
   */
  private _followUps(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0 && entry.length <= FOLLOW_UP_MAX_LENGTH)
      .slice(0, FOLLOW_UP_LIMIT);
  }
}
